import * as styles from "./DateRecurrenceExceptions.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import { NumberField } from "@frontend/components/atoms/NumberField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@frontend/components/atoms/Select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useLingui } from "@lingui/react/macro";

import type {
  RecurrenceException,
  RecurrenceExceptionType,
} from "./DateRecurrencePicker.types";

interface DateRecurrenceExceptionsProps {
  exceptions: RecurrenceException[];
  onChange: (exceptions: RecurrenceException[]) => void;
  locale?: string;
}

export function DateRecurrenceExceptions({
  exceptions,
  onChange,
  locale,
}: DateRecurrenceExceptionsProps): JSX.Element {
  const { t } = useLingui();

  const types: {
    value: RecurrenceExceptionType;
    label: string;
    icon: IconName;
  }[] = [
    { value: "holidays", label: t`Holidays`, icon: "GraduationCap" },
    { value: "dates", label: t`Dates`, icon: "CalendarRange" },
    { value: "yearlydates", label: t`Yearly dates`, icon: "CalendarSync" },
    { value: "weeks", label: t`Weeks`, icon: "SkipForward" },
  ];

  const holidaysTaken = exceptions.some(({ type }) => "holidays" === type);

  function iconOf(type: RecurrenceExceptionType): IconName {
    return types.find(({ value }) => value === type)?.icon ?? "CalendarRange";
  }

  function labelOf(type: RecurrenceExceptionType): string {
    return types.find(({ value }) => value === type)?.label ?? "";
  }

  function choicesFor(exception: RecurrenceException) {
    return holidaysTaken && "holidays" !== exception.type
      ? types.filter(({ value }) => "holidays" !== value)
      : types;
  }

  function update(index: number, patch: Partial<RecurrenceException>) {
    onChange(
      exceptions.map((exception, position) =>
        position === index ? { ...exception, ...patch } : exception,
      ),
    );
  }

  function remove(index: number) {
    onChange(exceptions.filter((_, position) => position !== index));
  }

  function add() {
    onChange([...exceptions, { type: "dates", start: null, stop: null }]);
  }

  return (
    <div data-slot="date-recurrence-exceptions" className={styles.exceptions}>
      <span className={styles.sectionTitle}>{t`Exceptions`}</span>

      {exceptions.map((exception, index) => (
        <div className={styles.exception} key={exception.id ?? `new-${index}`}>
          <div className={styles.exceptionType}>
            <Select
              items={choicesFor(exception)}
              size="sm"
              value={exception.type}
              onValueChange={(next) =>
                update(index, { type: next as RecurrenceExceptionType })
              }
            >
              <Tooltip>
                <TooltipTrigger
                  render={
                    <SelectTrigger
                      hideIcon
                      className={styles.exceptionTypeTrigger}
                      aria-label={labelOf(exception.type)}
                    >
                      <Icon name={iconOf(exception.type)} size="sm" />
                    </SelectTrigger>
                  }
                />

                <TooltipContent>{labelOf(exception.type)}</TooltipContent>
              </Tooltip>

              <SelectContent alignItemWithTrigger={false} popupWidth="content">
                {choicesFor(exception).map((type) => (
                  <SelectItem
                    key={type.value}
                    value={type.value}
                    leftSection={<Icon name={type.icon} size="sm" />}
                  >
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {("dates" === exception.type || "yearlydates" === exception.type) && (
            <div className={styles.exceptionRange}>
              <DatePicker
                range
                layout="text"
                locale={locale}
                value={[exception.start || null, exception.stop || null]}
                onValueChange={([start, stop]) =>
                  update(index, { start, stop })
                }
              >
                <DatePickerTrigger>
                  <DatePickerValue />
                </DatePickerTrigger>
                <DatePickerContent />
              </DatePicker>
            </div>
          )}

          {"weeks" === exception.type && (
            <div className={styles.exceptionRange}>
              <NumberField
                layout="text"
                min={0}
                max={99}
                value={exception.nbWeeks ?? 0}
                onValueChange={(next) => update(index, { nbWeeks: next ?? 0 })}
              />

              <span className={styles.unit}>{t`week(s) skipped`}</span>
            </div>
          )}

          <Button
            className={styles.exceptionRemove}
            variant="ghost"
            size="sm"
            aria-label={t`Remove`}
            onClick={() => remove(index)}
            leftSection={<Icon name="X" size="sm" />}
          />
        </div>
      ))}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={add}
          leftSection={<Icon name="Plus" size="sm" />}
          label={t`Add an exception`}
        />
      </div>
    </div>
  );
}
